import React, { useEffect, useRef, useState } from 'react';
import { useGetSet, useIntersection } from 'react-use';
import { Common } from '@thecvlb/design-system';
import HTMLReactParser from 'html-react-parser';

import { useGetSignableDocumentsQuery } from 'services/general/general';
import { SignableDocuments } from 'services/general/general.types';

import { BasicSignupStepProps } from 'containers/SignUp/Content/content.types';
import Loader from 'shared/Loader';
import { notifyToast } from 'shared/Toast/Toast';

import useSubmitOrchestrateForm from 'hooks/useSubmitOrchestrateForm';
import { FlowTypes } from 'utils/enums';

const Agreement: React.FC<BasicSignupStepProps> = ({ selectedFlow, moveToStep }) => {
  const { data, isFetching } = useGetSignableDocumentsQuery();

  const [isRead, toggleRead] = useState(false);
  const [isAgreed, toggle] = useState(false);
  const [finalState, setFinalState] = useGetSet<boolean | null>(null);

  const endOfAgreement = useRef<HTMLSpanElement>(null);

  const { send, isLoading } = useSubmitOrchestrateForm();

  const intersection = useIntersection(endOfAgreement as React.RefObject<HTMLElement>, {
    root: null,
    rootMargin: '0px',
    threshold: 1
  });
  const isOptaviaFlow = selectedFlow === FlowTypes.WeightManagementFlowOptavia;

  const getDocumentType = (): SignableDocuments => {
    if (isOptaviaFlow) {
      return 'hipaa-marketing-agreement';
    }
    return 'medicaid-waiver-terms';
  };
  const document = data?.data.find((item) => item.type === getDocumentType());

  const handleSubmit = (value: boolean) => {
    setFinalState(value);
    send('agreements', { hipaaMarketingAgreed: value }, () => {
      moveToStep('next');
    });
  };

  const handleClick = () => {
    if (!isRead || isFetching) {
      notifyToast('Scroll down to acknowledge and agree.', {
        position: 'top-center'
      });
    }
  };

  useEffect(() => {
    if (intersection?.isIntersecting && !!document) {
      toggleRead(true);
    }
  }, [intersection?.isIntersecting]);

  return (
    <div className="flex h-full flex-col gap-4">
      <Loader isVisible={isFetching} />
      <h1 className="wm-signup-title" data-testid="header">
        {isOptaviaFlow ? 'HIPAA Marketing Authorization' : 'Agreement for Self-Payment of Services'}
      </h1>
      {!isOptaviaFlow && (
        <Common.Alert type="info" colorableBackground>
          To continue, please scroll down and check the box to agree to terms.
        </Common.Alert>
      )}
      <div
        className="max-h-[60vh] overflow-auto rounded-xl border bg-white p-3 text-mBase text-primary-700 md:text-base"
        data-testid="agreement_block"
      >
        {HTMLReactParser(document?.html || '')}
        <span ref={endOfAgreement}></span>
      </div>
      <button onClick={handleClick}>
        <Common.Checkbox
          checked={isAgreed}
          className="text-start"
          color="blue"
          disabled={!isRead || isFetching}
          onChange={() => toggle((p) => !p)}
        >
          I acknowledge and agree to the statements written above.
        </Common.Checkbox>
      </button>
      <div className="mx-auto flex max-w-[215px] flex-col gap-3">
        <Common.Button
          className="mt-auto !w-full"
          color="blue"
          dataTestId="continue_btn"
          disabled={isFetching || !isAgreed}
          isLoading={isLoading && !!finalState()}
          fullWidthOnMobile
          onClick={() => handleSubmit(true)}
        >
          Agree and continue
        </Common.Button>
        <Common.Button
          color="white-alt"
          dataTestId="continue_btn"
          isLoading={isLoading && !isAgreed}
          fullWidthOnMobile
          onClick={() => handleSubmit(false)}
        >
          Continue without agreeing
        </Common.Button>
      </div>
    </div>
  );
};

export default Agreement;
